import * as React from "react";
import { Input } from "@/components/ui/input";

export type MaskType = "cpf" | "cnpj" | "cpf_cnpj" | "phone" | "mobile" | "cep";

const masks: Record<MaskType, { pattern: RegExp; format: (value: string) => string; maxLength: number }> = {
  cpf: {
    pattern: /\D/g,
    format: (value: string) => {
      return value
        .replace(/(\d{3})(\d)/, "$1.$2")
        .replace(/(\d{3})(\d)/, "$1.$2")
        .replace(/(\d{3})(\d{1,2})$/, "$1-$2");
    },
    maxLength: 14,
  },
  cnpj: {
    pattern: /\D/g,
    format: (value: string) => {
      return value
        .replace(/(\d{2})(\d)/, "$1.$2")
        .replace(/(\d{3})(\d)/, "$1.$2")
        .replace(/(\d{3})(\d)/, "$1/$2")
        .replace(/(\d{4})(\d{1,2})$/, "$1-$2");
    },
    maxLength: 18,
  },
  cpf_cnpj: {
    pattern: /\D/g,
    format: (value: string) => {
      const digits = value.replace(/\D/g, "");
      if (digits.length <= 11) {
        // CPF
        return digits
          .replace(/(\d{3})(\d)/, "$1.$2")
          .replace(/(\d{3})(\d)/, "$1.$2")
          .replace(/(\d{3})(\d{1,2})$/, "$1-$2");
      } else {
        // CNPJ
        return digits
          .replace(/(\d{2})(\d)/, "$1.$2")
          .replace(/(\d{3})(\d)/, "$1.$2")
          .replace(/(\d{3})(\d)/, "$1/$2")
          .replace(/(\d{4})(\d{1,2})$/, "$1-$2");
      }
    },
    maxLength: 18,
  },
  phone: {
    pattern: /\D/g,
    format: (value: string) => {
      const digits = value.replace(/\D/g, "");
      if (digits.length <= 10) {
        return digits
          .replace(/(\d{2})(\d)/, "($1) $2")
          .replace(/(\d{4})(\d)/, "$1-$2");
      }
      return digits
        .replace(/(\d{2})(\d)/, "($1) $2")
        .replace(/(\d{5})(\d)/, "$1-$2");
    },
    maxLength: 15,
  },
  mobile: {
    pattern: /\D/g,
    format: (value: string) => {
      return value
        .replace(/(\d{2})(\d)/, "($1) $2")
        .replace(/(\d{5})(\d)/, "$1-$2");
    },
    maxLength: 15,
  },
  cep: {
    pattern: /\D/g,
    format: (value: string) => {
      return value.replace(/(\d{5})(\d)/, "$1-$2");
    },
    maxLength: 9,
  },
};

export const unmask = (value: string): string => {
  return value.replace(/\D/g, "");
};

export const applyMask = (value: string, maskType: MaskType): string => {
  const mask = masks[maskType];
  if (!mask) return value;
  
  const cleanValue = value.replace(mask.pattern, "");
  return mask.format(cleanValue);
};

interface MaskedInputProps extends Omit<React.ComponentProps<"input">, "onChange"> {
  mask: MaskType;
  value?: string;
  onChange?: (value: string) => void;
  onValueChange?: (maskedValue: string, unmaskedValue: string) => void;
}

export const MaskedInput = React.forwardRef<HTMLInputElement, MaskedInputProps>(
  ({ mask, value = "", onChange, onValueChange, ...props }, ref) => {
    const maskConfig = masks[mask];

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const rawValue = e.target.value;
      const cleanValue = rawValue.replace(maskConfig.pattern, "");
      const maskedValue = maskConfig.format(cleanValue);
      
      onChange?.(maskedValue);
      onValueChange?.(maskedValue, cleanValue);
    };

    return (
      <Input
        ref={ref}
        value={value}
        onChange={handleChange}
        maxLength={maskConfig.maxLength}
        {...props}
      />
    );
  }
);

MaskedInput.displayName = "MaskedInput";
